import { UNKNOWN_SITE_NAME, UNKNOWN_AREA_NAME } from "../constants/constants";

export const fetchAssociatedStreams = async (appearance, fovList, roiListFromRedux, cameraList, licenses) => {
  const currentAppearance = appearance;

  if (!currentAppearance || !currentAppearance.deployments || currentAppearance.deployments.length === 0) {
    return null;
  }

  // Use fovList from Redux instead of deviceList
  const fovResponse = fovList || [];

  if (!fovResponse || fovResponse.length === 0) {
    return null;
  }

  // Use cameraList to get parent device information (includes location data)
  const cameras = cameraList || [];

  // Use ROI data from Redux instead of fetching via API
  const roiList = roiListFromRedux;

  // Create camera entries for each deployment (even if same fovid, different ROIs)
  const enrichedCameras = [];

  currentAppearance.deployments.forEach((deployment) => {
    const usedAppearances = deployment?.config?.usedAppearances;
    const currentAppConfig = usedAppearances?.[currentAppearance?.id] || (usedAppearances ? Object.values(usedAppearances)[0] : null);
    const embeddingVersion = typeof currentAppConfig === "object" ? currentAppConfig?.embeddingVersion : null;

    if (roiList && Array.isArray(roiList)) {
      // Find the ROI that matches this deployment's deviceid
      const matchingRoi = roiList.find((roi) => roi.id === deployment.deviceid);
      const fovid = matchingRoi?.parentdeviceid;
      const matchingFov = fovList?.find((fov) => fov.id === fovid);
      // Find the parent camera to get location information
      const parentCamera = cameras.find((cam) => cam.id === matchingFov.parentdeviceid);

      // Create a camera structure from the FOV data
      // Structure it similarly to the device format for consistency with downstream code
      const cameraFromFov = {
        id: matchingFov.parentdeviceid,
        fovs: [matchingFov],
        // Use parent camera's customdata if available (contains location info), otherwise use FOV's customdata
        customdata: parentCamera?.customdata || matchingFov.customdata,
        // Include other relevant properties from the FOV or parent camera
        name: matchingFov.name || parentCamera?.name || "",
        state: matchingFov.state || parentCamera?.state,
        providerid: matchingFov.providerid || parentCamera?.providerid,
        // Include full parent camera object so downstream rendering can show camera name/id
        parentCamera
      };

      if (matchingRoi && matchingRoi.customdata) {
        // Create a camera entry for this deployment with its specific ROI
        enrichedCameras.push({
          ...cameraFromFov,
          roiCustomdata: matchingRoi.customdata,
          deploymentId: deployment.id,
          deploymentDeviceId: deployment.deviceid,
          embeddingVersion,
          deploymentStatus: deployment.status
        });
      }
    }
  });


  // Filter enrichedCameras based on active licenses from Redux
  const filteredEnrichedCameras = enrichedCameras.filter((camera) => {
    if (!camera.fovs || !Array.isArray(camera.fovs)) {
      return false;
    }

    // Check if any FOV in this camera has at least one active license
    return camera.fovs.some((fov) => {
      // Get licenses array from FOV
      const fovLicenses = fov.licenses;
      
      // If FOV has no licenses, exclude this camera
      if (!fovLicenses || !Array.isArray(fovLicenses) || fovLicenses.length === 0) {
        return false;
      }

      // Check if at least one license in FOV's licenses array is active in Redux
      return fovLicenses.some((fovLicenseSku) => {
        // Find matching license in Redux licenses array
        const matchingLicense = licenses?.find((lic) => lic.sku === fovLicenseSku);
        // Check if the license exists and has active state
        return matchingLicense && matchingLicense?.state === "active";
      });
    });
  });

  return filteredEnrichedCameras;
};

export const fetchImageUrls = async (roiList) => {
  // Group cameras by site name
  const groupedBySite = {};

  for (const camera of roiList) {
    const siteName = camera.customdata?.location?.site?.name || UNKNOWN_SITE_NAME;

    if (!groupedBySite[siteName]) {
      groupedBySite[siteName] = [];
    }

    // For each FOV in the camera, create an ROI object
    if (camera.fovs) {
      for (const fov of camera.fovs) {
        // Extract areaName from camera customdata (location info is here)
        const areaName = camera.customdata?.location?.area?.name || UNKNOWN_AREA_NAME;

        const roiObject = {
          ...camera,
          ...fov,
          name: fov.name,
          areaName: areaName,
          tagSiteName: siteName,
          // Merge both customdata: polygon from roiCustomdata, location from camera customdata
          customdata: {
            ...(camera.customdata || {}),
            ...(camera.roiCustomdata || {})
          }
        };

        groupedBySite[siteName].push(roiObject);
      }
    }
  }

  // Transform to filteredRoiList structure: [{siteName, roiList: [[roi1, roi2], [roi3]]}]
  const transformed = Object.keys(groupedBySite).map((siteName) => {
    const rois = groupedBySite[siteName];

    // Group ROIs by camera (same parentdeviceid or id)
    const groupedByCamera = {};
    rois.forEach((roi) => {
      const cameraKey = roi.parentdeviceid || roi.id;
      if (!groupedByCamera[cameraKey]) {
        groupedByCamera[cameraKey] = [];
      }
      groupedByCamera[cameraKey].push(roi);
    });

    // Convert to array of arrays
    const roiList = Object.values(groupedByCamera);

    return {
      siteName,
      roiList
    };
  });

  return transformed;
};
